import { FC, useState } from "react";
import { DataManager } from "../dataManager";
import { CardPack, ResourceKind } from "../types";
import "./Shop.css";
import Sprite from "./Sprite";
import { GetPlayable } from "../constants";

export interface ShopProps {
  onClose: () => void;
  onBuyPack: (pack: CardPack) => boolean;
  currentMoney: number;
  unlockedResources: Set<ResourceKind>;
  isOutOfCards?: boolean;
}

const Shop: FC<ShopProps> = ({
  onClose,
  onBuyPack,
  currentMoney,
  unlockedResources,
  isOutOfCards = false,
}) => {
  const cardPacks = DataManager.getInstance().cardPacks;

  const canBuyPack = (pack: CardPack): boolean => {
    if (currentMoney < pack.cost) return false;
    return pack.required.every((resource) => unlockedResources.has(resource));
  };

  const [selectedPack, setSelectedPack] = useState<CardPack | null>(
    cardPacks.findLast(canBuyPack) || null,
  );

  const getMissingRequirements = (pack: CardPack): ResourceKind[] => {
    return pack.required.filter((resource) => !unlockedResources.has(resource));
  };

  const handleBuyPack = () => {
    if (selectedPack && canBuyPack(selectedPack)) {
      const success = onBuyPack(selectedPack);
      if (success) {
        onClose();
      }
    }
  };

  return (
    <div className="shop-overlay">
      <div className="shop-backdrop" onClick={onClose} />
      <div className="shop-container">
        <div className="shop-header">
          <h2>Shop</h2>
          <button className="shop-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        {isOutOfCards && (
          <div className="shop-out-of-cards-message">
            You're out of cards! Purchase a pack to continue.
          </div>
        )}

        <div className="shop-content">
          <div className="shop-money flex items-center gap-1">
            <Sprite
              filename="/images/resources_20.png"
              x={5}
              y={0}
              width={20}
              height={20}
              className="inline-block"
            />
            Money: ${currentMoney}
          </div>

          <div className="shop-packs-list">
            {cardPacks.map((pack, index) => {
              const isSelected = selectedPack === pack;
              const canBuy = canBuyPack(pack);
              const missingReqs = getMissingRequirements(pack);

              return (
                <div
                  key={index}
                  className={`shop-pack-entry ${isSelected ? "selected" : ""} ${
                    !canBuy ? "disabled" : ""
                  }`}
                  onClick={() => setSelectedPack(pack)}
                >
                  <span className="pack-number">{index + 1}</span>
                  <span className="pack-name">{pack.name}</span>
                  <span className="pack-cost">${pack.cost}</span>
                  {missingReqs.length > 0 && (
                    <span className="pack-missing">
                      Missing: {missingReqs.join(", ")}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {selectedPack && (
            <div className="shop-pack-details">
              <h3>{selectedPack.name}</h3>
              <p>{selectedPack.description}</p>
              <div className="pack-requirements">
                {selectedPack.required.length > 0 ? (
                  <>
                    <strong>Requirements:</strong>{" "}
                    {selectedPack.required.join(", ")}
                  </>
                ) : (
                  <em>No requirements</em>
                )}
              </div>
              <div className="pack-contents">
                <strong>Contains:</strong>
                <ul className="flex">
                  {selectedPack.cards
                    .sort((a, b) =>
                      a.template.name.localeCompare(b.template.name),
                    )
                    .map((cardDef, index, { length }) => (
                      <li key={index} className="mr-1">
                        {GetPlayable(cardDef.template.name).label}
                        {index < length - 1 && ", "}
                      </li>
                    ))}
                </ul>
              </div>
              <button
                className={`shop-buy-btn ${
                  canBuyPack(selectedPack) ? "" : "disabled"
                }`}
                onClick={handleBuyPack}
                disabled={!canBuyPack(selectedPack)}
              >
                {canBuyPack(selectedPack)
                  ? `Buy for $${selectedPack.cost}`
                  : currentMoney < selectedPack.cost
                    ? "Insufficient funds"
                    : "Missing requirements"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Shop;
